import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, SimpleGrid, useToast, Flex, Button } from '@chakra-ui/react';
import DynamicTable from '../../../components/table/DynamicTable.js';
import { columnsDataInscriptions } from '../../../variables/columnsDataInscriptions.js';
import { getData, deleteData, patchData } from '../../../utils/httpClient.js';
import { useSearch } from '../../../contexts/SearchContext.js';
import CreateInscriptionModal from '../../../components/inscriptions/CreateInscriptionModal.js';
import ConfirmModal from '../../../components/modal/ConfirmModal.js';

// Acciones sobre inscripciones:
//   - EDITAR: eliminada por requerimiento del cliente (Módulo 6).
//   - BAJA: marca status='inactive' + discharge_date (endpoint PATCH /inscriptions/:id/discharge).
//   - ELIMINAR: NO hace DELETE físico. Marca status='cancelled' + discharge_date
//     (endpoint DELETE /inscriptions/:id, modificado para hacer baja lógica).
export default function Inscriptions() {
  const { searchQuery } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    createModal: false,
    confirmDelete: false,
    confirmDischarge: false,
  });
  const [selectedInscription, setSelectedInscription] = useState(null);

  const rowsPerPage = 10;
  const toast = useToast();

  const latestRequestRef = useRef(0);

  const getInscriptions = useCallback(async (searchData) => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await getData(
        `/inscriptions?page=${page + 1}&limit=${rowsPerPage}&data=${searchData}`,
      );
      if (requestId !== latestRequestRef.current) return;
      setData(response?.payload?.inscriptions || []);
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      toast({
        title: 'Error al obtener inscripciones',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [page, rowsPerPage, toast]);

  const handleCreateInscription = async () => {
    await getInscriptions('');
  };

  // ELIMINAR (baja lógica). El endpoint DELETE en backend fue adaptado
  // para no hacer borrado físico, sólo status='cancelled' + discharge_date.
  const handleDeleteInscription = async () => {
    if (!selectedInscription) return;

    try {
      await deleteData(`/inscriptions/${selectedInscription.id}`);
      toast({
        title: 'Inscripción eliminada (baja lógica)',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setModalState((prev) => ({ ...prev, confirmDelete: false }));
      setSelectedInscription(null);
      await getInscriptions('');
    } catch (error) {
      toast({
        title: 'Error al eliminar inscripción',
        description: error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // BAJA: distinta a ELIMINAR. Mantiene la inscripción como histórico "dada de baja".
  const handleDischargeInscription = async () => {
    if (!selectedInscription) return;

    try {
      await patchData(`/inscriptions/${selectedInscription.id}/discharge`, {});
      toast({
        title: 'Inscripción dada de baja',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setModalState((prev) => ({ ...prev, confirmDischarge: false }));
      setSelectedInscription(null);
      await getInscriptions('');
    } catch (error) {
      toast({
        title: 'Error al dar de baja la inscripción',
        description: error.message || 'Ha ocurrido un error inesperado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getInscriptions(searchQuery);
  }, [page, searchQuery, getInscriptions]);

  const openCreateModal = () => {
    setModalState((prev) => ({ ...prev, createModal: true }));
  };

  const closeCreateModal = () => {
    setModalState((prev) => ({ ...prev, createModal: false }));
  };

  const openDeleteModal = (inscription) => {
    setSelectedInscription(inscription);
    setModalState((prev) => ({ ...prev, confirmDelete: true }));
  };

  const closeDeleteModal = () => {
    setModalState((prev) => ({ ...prev, confirmDelete: false }));
    setSelectedInscription(null);
  };

  const openDischargeModal = (inscription) => {
    setSelectedInscription(inscription);
    setModalState((prev) => ({ ...prev, confirmDischarge: true }));
  };

  const closeDischargeModal = () => {
    setModalState((prev) => ({ ...prev, confirmDischarge: false }));
    setSelectedInscription(null);
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="flex-end" mb={4}>
        <Button
          colorScheme="blue"
          onClick={openCreateModal}
          size="lg"
          fontWeight="bold"
        >
          Nueva Inscripción
        </Button>
      </Flex>

      <SimpleGrid
        mb="20px"
        columns={{ sm: 1 }}
        spacing={{ base: '20px', xl: '20px' }}
      >
        <DynamicTable
          columnsData={columnsDataInscriptions}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          showRowInfo={false}
          // onEdit removido por requerimiento del cliente (Módulo 6).
          onDischarge={openDischargeModal}
          onDelete={openDeleteModal}
        />
      </SimpleGrid>

      <CreateInscriptionModal
        isOpen={modalState.createModal}
        onClose={closeCreateModal}
        onConfirm={handleCreateInscription}
      />

      <ConfirmModal
        isOpen={modalState.confirmDischarge}
        onClose={closeDischargeModal}
        onConfirm={handleDischargeInscription}
        header="Dar de baja inscripción"
        message={`¿Confirmás dar de baja la inscripción de ${
          selectedInscription?.student?.person?.name || 'este estudiante'
        }? Se registrará la fecha de baja.`}
        confirmText="Dar de baja"
        cancelText="Cancelar"
      />

      <ConfirmModal
        isOpen={modalState.confirmDelete}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteInscription}
        header="Eliminar inscripción"
        message={`¿Confirmás eliminar (baja lógica) la inscripción de ${
          selectedInscription?.student?.person?.name || 'este estudiante'
        }? El registro no se borra físicamente.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </Box>
  );
}
